// src/lib/utils/extractLinks/extractLinks.ts (Version React/Web - DOM)

import { blackListedUrlStart } from "../../managers/mail/types/const";
import {
    _cleanText,
    _extractTargetUrl,
    CID_PLACEHOLDER,
    CID_REGEX,
    computeDedupKey,
} from "./extractLinks.types";
import type { ExtractLinksFunction, LinkObject } from "./extractLinks.types";

export type { ExtractLinksFunction } from "./extractLinks.types";

export const extractLinks: ExtractLinksFunction = (htmlInput) => {
    if (!htmlInput.trim()) {
        return [];
    }

    // Prétraitement pour neutraliser les références cid: avant de créer l'élément DOM
    const processedHtml = htmlInput.replace(CID_REGEX, CID_PLACEHOLDER);

    // Créer un élément temporaire pour parser le HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = processedHtml;

    // Extraire tous les liens <a>
    const links = tempDiv.querySelectorAll("a[href]");
    const linkObjects: LinkObject[] = [];
    const seenUrls = new Set<string>(); // Pour tracker les URLs déjà rencontrées

    links.forEach((link: Element) => {
        const url = _cleanText(link.getAttribute("href") || "");

        // Skip si l'URL est vide ou déjà présente (dédup sur clé normalisée)
        const dedupKey = computeDedupKey(url);
        if (!url || seenUrls.has(dedupKey)) {
            return;
        }

        // Cible réelle (déballage récursif des redirections + décodage) — utilisée pour la blacklist
        // ET le nom d'affichage de repli (parité backend : les deux se basent sur la cible, pas le wrapper)
        const targetUrl = _extractTargetUrl(url);

        // Vérifier si l'URL cible commence par une URL blacklistée
        const isBlacklisted = blackListedUrlStart.some((blacklistedUrl) =>
            targetUrl.startsWith(blacklistedUrl),
        );

        if (isBlacklisted) {
            return; // Skip ce lien
        }

        // Nom d'affichage : texte du lien, sinon alt d'image, sinon innerText
        let displayName = _cleanText(link.textContent || "");
        if (!displayName) {
            const img = link.querySelector("img[alt]");
            if (img) {
                displayName = _cleanText(img.getAttribute("alt") || "");
            }
        }
        if (!displayName) {
            displayName = _cleanText((link as HTMLElement).innerText || "");
        }

        // Repli sur l'URL cible si pas de nom, ou si le nom == href brut
        const name =
            displayName && displayName !== url ? displayName : targetUrl;

        seenUrls.add(dedupKey); // Marquer cette URL comme vue
        linkObjects.push({
            url: url,
            name: name,
        });
    });

    return linkObjects;
};
